import { OpenInNew } from "@mui/icons-material";
import { Button } from "@mui/material";
import styled, { createGlobalStyle } from "styled-components/macro";
import {
  useEffectOnce,
  useFocusRange,
  useProcessesInVisibleGroups,
} from "utils/customHooks/genericHooks";
import { useEffect, useState } from "react";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { AUTO_DOWNLOAD_IMG } from "./SnapshotElementButton";
import { SnapshotsCSVUploader } from "./SnapshotsCSVUploader";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import { FONT_SIZE_MEDIUM } from "constants/constants";
import { useSelector } from "store/useSelector";
import { useLocation } from "react-router";
import { useDispatch } from "react-redux";
import { setFocusRange } from "providers/GlobalStateProvider";
import { ProcessesPicker } from "./ProcessesPicker";
import { ProcessGroupsPicker } from "./ProcessGroupsPicker";
import { RangeSelectorCardHubletPage } from "pages/HubletPage/DatepickerAndCharts";

/** simple form page that lets you download Timeline images by specifying processes and time range to view */
function SnapshotsPage() {
  const allProcesses = useProcessesInVisibleGroups();
  const allProcessesLabeled = allProcesses.map(p => ({
    label: p.name,
    id: p.id,
  }));
  const allGroups = useSelector(s => s.legacy.allGroups);
  const allGroupsLabeled = allGroups.map(pg => ({
    label: pg.shortName ?? pg.longName,
    id: pg.id ?? pg.shortName ?? pg.longName,
    processIds: pg.processes.map(p => p.id),
  }));
  const focusRange = useFocusRange();

  // sync selected processes + groups to url params pids, pgids
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);

  // set focusRange from url params once if it's null
  const dispatch = useDispatch();
  useEffectOnce({
    callback: () => {
      const ge = urlSearchParams.get("ge");
      const lt = urlSearchParams.get("lt");
      if (ge && lt) {
        dispatch(setFocusRange({ ge: Number(ge), lt: Number(lt) }));
      }
    },
    shouldRun: !focusRange,
    dependencies: [focusRange],
  });

  const initialSelectedProcessIds =
    urlSearchParams.get("pids")?.split(",") ?? [];
  const initialSelectedProcesses = allProcessesLabeled.filter(p =>
    initialSelectedProcessIds.includes(p.id)
  );

  const [selectedProcesses, setSelectedProcesses] = useState<
    {
      label: string;
      id: string;
    }[]
  >(initialSelectedProcesses);

  const initialSelectedProcessGroupIds =
    urlSearchParams.get("pgids")?.split(",") ?? [];
  const initialSelectedProcessGroups = allGroupsLabeled.filter(pg =>
    initialSelectedProcessGroupIds.includes(pg.id)
  );

  const [selectedGroups, setSelectedGroups] = useState<
    {
      label: string;
      id: string;
      processIds: string[];
    }[]
  >(initialSelectedProcessGroups);

  // sync url params pids, pgids when we change state
  useEffect(() => {
    const selectedProcessIds = selectedProcesses.map(p => p.id);
    const selectedProcessGroupIds = selectedGroups.map(pg => pg.id);
    const newSearchParams = new URLSearchParams(location.search);
    if (selectedProcessIds.length) {
      newSearchParams.set("pids", selectedProcessIds.join(","));
    } else {
      newSearchParams.delete("pids");
    }
    if (selectedProcessGroupIds.length) {
      newSearchParams.set("pgids", selectedProcessGroupIds.join(","));
    } else {
      newSearchParams.delete("pgids");
    }
    if (selectedProcessIds.length || selectedProcessGroupIds.length) {
      window.history.replaceState(
        {},
        "",
        `${location.pathname}?${newSearchParams}`
      );
    }
  }, [selectedProcesses, selectedGroups, location.search, location.pathname]);

  // after mount, wait for allGroups/allProcesses to become available, then grab processes & groups from url & sync to state once
  useEffectOnce({
    callback: () => {
      const selectedProcessIds = urlSearchParams.get("pids")?.split(",") ?? [];
      const selectedProcessGroupIds =
        urlSearchParams.get("pgids")?.split(",") ?? [];

      const selectedProcesses = allProcessesLabeled.filter(p =>
        selectedProcessIds.includes(p.id)
      );
      const selectedGroups = allGroupsLabeled.filter(pg =>
        selectedProcessGroupIds.includes(pg.id)
      );

      setSelectedProcesses(selectedProcesses);
      setSelectedGroups(selectedGroups);
    },
    shouldRun: Boolean(allGroupsLabeled.length && allProcessesLabeled.length),
    dependencies: [allGroupsLabeled.length, allProcessesLabeled.length],
  });

  const {
    downloadTimelineImgHref,
    downloadPnlImgHref,
    downloadPnlTimelineImgHref,
  } = useAutoSnapshotImageUrls(selectedProcesses);

  if (!focusRange) {
    return null;
  }

  function handleDownloadImg(href: string) {
    if (!focusRange) {
      return;
    }
    const newWindow = window.open(href, "_blank", "noopener,noreferrer");
    if (newWindow) {
      newWindow.blur(); // open tab in background
    }
  }

  function handleDownloadTimelineImg() {
    handleDownloadImg(downloadTimelineImgHref);
  }
  function handleDownloadPnlImg() {
    handleDownloadImg(downloadPnlImgHref);
  }
  function handleDownloadPnlTimelineImg() {
    handleDownloadImg(downloadPnlTimelineImgHref);
  }

  return (
    <Styles>
      <div className="card cardItem">
        <h1>
          <strong>Take a snapshot</strong>
        </h1>
        <p>
          <strong>Select time range & processes:</strong>
        </p>
        <br />
        <div className="daterangepicker-container">
          <RangeSelectorCardHubletPage autoSubmit={true} />
        </div>
        <br />
        <ProcessesPicker
          {...{
            allProcessesLabeled,
            selectedProcesses,
            setSelectedProcesses,
            onSelectProcess: () => {
              setSelectedGroups([]);
            },
          }}
        />
        <ProcessGroupsPicker
          {...{
            allProcessesLabeled,
            allGroupsLabeled,
            selectedGroups,
            selectedProcesses,
            setSelectedGroups,
            setSelectedProcesses,
          }}
        />
        <br />
        <div className="flex">
          <Button
            onClick={handleDownloadTimelineImg}
            variant="contained"
            style={{ color: "white" }}
            endIcon={<OpenInNew />}
            disabled={selectedProcesses.length === 0}
          >
            Download Timeline Image
          </Button>
          <Button
            onClick={handleDownloadPnlImg}
            variant="contained"
            style={{ color: "white" }}
            endIcon={<OpenInNew />}
            disabled={selectedProcesses.length === 0}
          >
            Download P&L Image
          </Button>

          <Button
            onClick={handleDownloadPnlTimelineImg}
            variant="contained"
            style={{ color: "white" }}
            endIcon={<OpenInNew />}
            disabled={selectedProcesses.length === 0}
          >
            Download {"["}P&L, Timeline{"]"} Images
          </Button>
        </div>
      </div>
      <div className="card cardItem">
        <h1>
          <strong>Upload a CSV to take multiple snapshots</strong>
        </h1>
        <ErrorBoundary id="csv-upload">
          <SnapshotsCSVUploader />
        </ErrorBoundary>
      </div>
      <SnapshotsPageGlobalStyles />
    </Styles>
  );
}
const SnapshotsPageGlobalStyles = createGlobalStyle`
  /* hide the "Stay on Current Shift" toggle in range selector config menu (irrelevant on this page) */
  .rangeSelectorConfigMenu .MuiList-root > div:first-child {
    display: none;
  }
`;
const Styles = styled.div`
  display: flex;
  gap: 1em;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 2em auto;
  width: 100%;
  max-width: 900px;
  .Card_RangeSelector {
    margin: 0;
  }
  [class*="DateRangePickerStyles"] {
    justify-content: center;
  }
  .daterangepicker-container {
    margin: auto;
    width: 100%;
  }
  h1 {
    font-size: ${FONT_SIZE_MEDIUM}px;
  }
  button {
    text-transform: none;
  }
  .card {
    padding: 2em;
    display: flex;
    gap: 1em;
    flex-direction: column;
    justify-content: center;
    align-items: baseline;
    width: 100%;
    max-width: 600px;
    margin-bottom: 1em;
  }
  .flex {
    display: flex;
    flex-wrap: wrap;
    gap: 1em;
  }

  .download-template {
    display: flex;
    align-items: center;
    gap: 1em;
    a {
      display: flex;
      align-items: center;
      gap: 0.25em;
    }
  }

  pre {
    background: lightgrey;
    font-family: monospace;
    padding: 10px;
    line-height: 1.2em;
  }
`;
export default SnapshotsPage;

function useAutoSnapshotImageUrls(
  selectedProcesses: { label: string; id: string }[]
) {
  const allProcesses = useProcessesInVisibleGroups();
  const workScheduleId = allProcesses[0]?.workScheduleConfig.id;
  const focusRange = useFocusRange();
  const ckey = useCustomerKey();
  if (!focusRange) {
    return {
      downloadTimelineImgHref: "",
      downloadPnlImgHref: "",
      downloadPnlTimelineImgHref: "",
    };
  }
  const downloadTimelineImgHref = `/${ckey}/dashboard/fromSnapshots?view=tl&workSchedule=${workScheduleId}&processes=${selectedProcesses
    .map(p => p.id)
    .join(",")}&ge=${focusRange.ge}&lt=${
    focusRange.lt
  }&hideTrend=true&hidePnl=true&hideSummary=true&autoDownload=${
    AUTO_DOWNLOAD_IMG.timeline
  }&hideMetricsColumn=false`;

  const downloadPnlImgHref = `/${ckey}/dashboard/fromSnapshots?view=tl&workSchedule=${workScheduleId}&processes=${selectedProcesses
    .map(p => p.id)
    .join(",")}&ge=${focusRange.ge}&lt=${
    focusRange.lt
  }&hideTrend=true&hideTimeline=true&hideSummary=true&autoDownload=${
    AUTO_DOWNLOAD_IMG.pnl
  }`;

  const downloadPnlTimelineImgHref = `/${ckey}/dashboard/fromSnapshots?view=tl&workSchedule=${workScheduleId}&processes=${selectedProcesses
    .map(p => p.id)
    .join(",")}&ge=${focusRange.ge}&lt=${
    focusRange.lt
  }&hideTrend=true&hideSummary=true&autoDownload=${
    AUTO_DOWNLOAD_IMG.pnl
  }&autoDownload=${AUTO_DOWNLOAD_IMG.timeline}&hideMetricsColumn=false`;
  return {
    downloadTimelineImgHref,
    downloadPnlImgHref,
    downloadPnlTimelineImgHref,
  };
}
